import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, ChevronRight, RotateCw } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Field officer landing screen. Giant "Scan QR" CTA + recent scans list.
 */
export default function FieldHome() {
  const nav = useNavigate();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/field/scans", { params: { limit: 30 } });
      setScans(data.data || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load recent scans");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="field-home-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-flame-400 font-semibold">Field Officer</div>
        <h1 className="font-heading text-2xl font-bold text-white mt-1">Ready to scan.</h1>
        <p className="text-sm text-slate-400 mt-1">Scan a customer&apos;s QR code to view their cylinder and record a replacement.</p>
      </div>

      {/* Giant Scan CTA */}
      <button
        onClick={() => nav("/field/scan")}
        data-testid="field-home-scan-btn"
        className="w-full aspect-square max-h-64 rounded-3xl bg-gradient-to-br from-flame-500 to-flame-700 text-white shadow-2xl shadow-flame-500/20 flex flex-col items-center justify-center gap-4 active:scale-[0.98] transition-transform hover:brightness-110"
      >
        <div className="p-5 rounded-2xl bg-white/15 backdrop-blur">
          <ScanLine size={64} strokeWidth={1.6} />
        </div>
        <div className="text-center">
          <div className="font-heading text-xl font-bold">Scan QR</div>
          <div className="text-[11px] uppercase tracking-widest text-flame-100/90 mt-1">Point camera at customer card</div>
        </div>
      </button>

      {/* Recent scans */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Recent scans</div>
          <button
            onClick={load}
            data-testid="field-home-refresh-btn"
            className="text-slate-400 hover:text-slate-200 p-1"
            aria-label="Refresh"
          >
            <RotateCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm py-6 text-center">Loading…</div>
        ) : scans.length === 0 ? (
          <div className="text-slate-500 text-sm py-6 text-center border border-dashed border-slate-800 rounded-lg">
            No scans yet. Tap the button above to get started.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="field-recent-scans">
            {scans.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => nav(`/field/h/${s.household_id}`)}
                  data-testid={`field-recent-scan-${s.household_id}`}
                  className="w-full text-left bg-slate-900 border border-slate-800 rounded-lg px-3 py-3 flex items-center justify-between gap-3 hover:border-slate-700 active:bg-slate-800/70"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      {s.household_name || "—"}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 tabular-nums">
                      <span className="truncate">{s.meter_serial || "no meter"}</span>
                      {s.area_name && <><span>·</span><span className="truncate">{s.area_name}</span></>}
                    </div>
                    <div className="text-[10px] text-slate-600 mt-1 tabular-nums">
                      {new Date(s.scanned_at).toLocaleString()}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
